import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button } from '@alwindows/shared'
import {
  getScreenImageSrc,
  getScreenInstallCost,
  getScreenItems,
  getScreenOptions,
  type ScreenItem,
  type ScreenOption,
} from '@alwindows/shared/api/screens'
import { STUB_ITEMS, STUB_OPTIONS, STUB_SO } from './_stubs'
import { useQuote } from '../quote/QuoteContext'
import { ROUTE_SUMMARY } from '../routes'
import { trackEditWindow } from '../analytics'
import {
  effectiveInstallCost,
  getConfigCharacteristic,
  getConfigValue,
  lineDisplayName,
  lineTotal,
  prodFamFromConfig,
  type ScreenLine,
} from '../quote/types'
import { LineConfigurator } from './LineConfigurator'

/**
 * "Select screens" — the heart of the flow. Loads /v1/screen/items for every
 * sales order at the address and presents one row per window/door, grouped
 * the way the live site does (My windows / My doors).
 *
 * Each row is both the order checkbox AND the configurator: "Customise"
 * expands the row to show screen type / mesh / grill / colour inline (live —
 * every change commits straight to the quote so the fixed cart bar at the
 * bottom updates as the user picks options). A customised row can be applied
 * to all other windows or doors in one click, at each item's own SAP prices
 * (legacy "Apply to all windows in my home").
 *
 * No quantity control: each row is an individual opening at the customer's
 * home — qty stays whatever the original order line carried.
 */
export function PricingCalc() {
  const { quote, setQuote } = useQuote()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Which row is expanded for inline customisation. One at a time — multi-
  // expand makes the page too tall and dilutes focus.
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  // Apply-to-all progress + outcome note (cleared when the panel changes).
  const [applyBusy, setApplyBusy] = useState(false)
  const [applyNote, setApplyNote] = useState<string | null>(null)
  // True once unmounted — guards the apply-to-all loop's trailing state
  // writes (it awaits a fetch per target, so the user can navigate away
  // mid-run; writing state after unmount warns + corrupts nothing useful).
  const unmountedRef = useRef(false)
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])

  useEffect(() => {
    const so = quote.salesOrderNo
    if (!so) {
      // In production the page demands a sales order — if there isn't one
      // the user hasn't completed step 1 yet, so we bounce them back. When
      // debug mode is on (wp-admin → Settings → A&L Windows → Debug mode)
      // the bounce is suppressed so a tester can type the URL and land
      // here without seeding a quote first. The page renders an
      // empty list in that case; paste a real SO into sessionStorage via
      // DevTools to see populated content:
      //   sessionStorage.setItem('al:screens:quote:v1', JSON.stringify({
      //     version: <QUOTE_VERSION>, salesOrderNo: 'YOUR_SO', lines: [], ...
      //   }))
      const debug = typeof window !== 'undefined' && Boolean(window.AL_WINDOWS_CONFIG?.debug)
      if (!debug) navigate('/', { replace: true })
      return
    }
    // Items already loaded for this SO. The quote is persisted to
    // sessionStorage, so on a remount (navigating to Summary/Payment and back,
    // or any re-mount) it restores with the customer's configured lines
    // intact. Refetching here would rebuild every line unconfigured via
    // lineFromItem and wipe that work — so skip. `itemsLoaded` is reset to
    // false in ScreenHome whenever a new sales order is chosen, so a genuine
    // SO change still triggers a fresh load. Guarding on this persisted state
    // (rather than a per-mount ref) is also what lets StrictMode's mount →
    // cleanup → mount cycle retry cleanly instead of cancelling the only fetch.
    if (quote.itemsLoaded) return

    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const items = await getScreenItems(so)
        if (cancelled) return

        const lines: ScreenLine[] = items.map((item) => lineFromItem(item))

        // Install cost is a single per-SO value, and `so` may be several
        // orders joined with ';' (every order found at the address) — so try
        // each order's own SO until one yields a fee. Mirrors legacy, where
        // whichever configured item first returned a non-zero install cost
        // won. The INSTALL_DOUBLE_FAM doubling is applied at display/submit
        // time via effectiveInstallCost(); this stores the BASE fee.
        let installCost = 0
        for (const itemSo of [...new Set(items.map((i) => i.SONo))]) {
          try {
            const fee = await getScreenInstallCost(itemSo)
            if (Number.isFinite(fee) && fee > 0) {
              installCost = fee
              break
            }
          } catch {
            // 404 is expected for some SOs; try the next order.
          }
        }

        if (cancelled) return
        setQuote((prev) => ({
          ...prev,
          lines,
          itemsLoaded: true,
          installCost,
          customer: prev.customer
            ? { ...prev.customer, salesOrg: items[0]?.SalesOrg ?? prev.customer.salesOrg }
            : prev.customer,
        }))
      } catch (err) {
        if (cancelled) return
        // itemsLoaded stays false on failure, so remounting (or otherwise
        // re-running this effect) retries rather than stranding the user on
        // the error state.
        setError(err instanceof Error ? err.message : 'Failed to load items.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [quote.salesOrderNo, quote.itemsLoaded, setQuote, navigate])

  const toggle = (key: string) => {
    setQuote((prev) => ({
      ...prev,
      lines: prev.lines.map((l) => (lineKey(l) === key ? { ...l, selected: !l.selected } : l)),
    }))
  }

  const updateLine = (key: string, next: ScreenLine) => {
    setQuote((prev) => ({
      ...prev,
      lines: prev.lines.map((l) => (lineKey(l) === key ? next : l)),
    }))
  }

  /**
   * The line exactly as it stood when the open panel was expanded. Live mode
   * commits every pick straight to the quote, so this is the only record of
   * what to put back if the user cancels. A ref, not state — nothing renders
   * from it, and it must not trigger a re-render mid-configuration.
   */
  const panelSnapshot = useRef<ScreenLine | null>(null)

  const openPanel = (key: string | null) => {
    panelSnapshot.current = key ? (quote.lines.find((l) => lineKey(l) === key) ?? null) : null
    setExpandedKey(key)
    setApplyNote(null)
  }

  /**
   * Cancel: undo everything picked since the panel opened — screen type,
   * sub-options, colour, and the auto-tick that configuring applies — then
   * close. Switching to ANOTHER row's panel is not a cancel and keeps the
   * changes; only this button rolls back.
   */
  const cancelPanel = () => {
    const snapshot = panelSnapshot.current
    if (snapshot) {
      const key = lineKey(snapshot)
      setQuote((prev) => ({
        ...prev,
        lines: prev.lines.map((l) => (lineKey(l) === key ? snapshot : l)),
      }))
    }
    openPanel(null)
  }

  /**
   * Legacy "Apply to all {windows|doors} in my home" (Editor.js applytoAll):
   * copy the source row's configuration onto every other item of the same
   * kind, at each item's OWN prices — every target's /v1/screen/options is
   * fetched and the matching screen type / mesh / grill / pet flap looked up
   * per item. Targets that can't take the chosen screen (no matching option,
   * fixed window, no ScreenApply, bi-fold family) are skipped, mirroring
   * legacy's STATUS_NOT_APPLICABLE / BI_FOLD exclusions.
   */
  const applyToAll = async (source: ScreenLine) => {
    const kind = source.sourceItem.ProdType
    const sourceKey = lineKey(source)
    const targets = quote.lines.filter(
      (l) =>
        lineKey(l) !== sourceKey &&
        l.sourceItem.ProdType === kind &&
        l.sourceItem.ScreenApply &&
        !l.sourceItem.FixedWindow &&
        prodFamFromConfig(l.sourceConfig) !== 'WBF',
    )
    if (targets.length === 0) return

    setApplyBusy(true)
    setApplyNote(null)
    const salesOrg = quote.customer?.salesOrg ?? ''
    const updates = new Map<string, ScreenLine>()

    for (const target of targets) {
      try {
        const options: ScreenOption[] =
          target.sourceItem.SONo === STUB_SO
            ? STUB_OPTIONS
            : await getScreenOptions({
                prodFam: prodFamFromConfig(target.sourceConfig),
                balRating: target.balRating || 'LOW',
                salesOrg,
                soNo: target.sourceItem.SONo,
                itemNo: target.sourceItem.ItemNo,
              })
        const opt = options.find((o) => o.ProdType === source.screenOption)
        if (!opt) continue

        // Sub-options carry over only when the target's screen actually
        // offers them (legacy findOptionForAllSelected returns {} otherwise),
        // priced from the TARGET's own option row.
        const num = (v: string | undefined) => parseFloat(v ?? '0') || 0
        const carry = (
          results: {
            ProdType: string
            Price: string
            NonTradeCharge: string
            Desc: string
            ProdFam: string
          }[],
          picked: string,
        ) => {
          const match = picked ? results.find((r) => r.ProdType === picked) : undefined
          return {
            type: match ? picked : '',
            price: num(match?.Price),
            nonTrade: num(match?.NonTradeCharge),
            desc: match?.Desc ?? '',
            prodFam: match?.ProdFam ?? '',
          }
        }
        const mesh = carry(opt.ScreenToMesh.results, source.meshType)
        const grill = carry(opt.ScreenToGrill.results, source.grillType)
        const petFlap = carry(opt.ScreenToPetFlap.results, source.petFlapType)

        updates.set(lineKey(target), {
          ...target,
          selected: true,
          screenOption: source.screenOption,
          meshType: mesh.type,
          grillType: grill.type,
          petFlapType: petFlap.type,
          hasTripleLock: source.hasTripleLock && num(opt.TripleLockCost) > 0,
          frameColour: source.frameColour,
          frameColourDesc: source.frameColourDesc,
          unitPrice: num(opt.Price),
          fitCost: num(opt.FitCost),
          tripleLockCost: num(opt.TripleLockCost),
          nonTradeCharge: num(opt.NonTradeCharge),
          meshPrice: mesh.price,
          meshNonTrade: mesh.nonTrade,
          grillPrice: grill.price,
          grillNonTrade: grill.nonTrade,
          petFlapPrice: petFlap.price,
          petFlapNonTrade: petFlap.nonTrade,
          screenOptionDesc: opt.Desc,
          meshDesc: mesh.desc,
          grillDesc: grill.desc,
          petFlapDesc: petFlap.desc,
          meshProdFam: mesh.prodFam,
          grillProdFam: grill.prodFam,
          petFlapProdFam: petFlap.prodFam,
        })
      } catch {
        // A target whose options fetch fails is skipped, not fatal — the
        // user can still customise it individually.
      }
    }

    // The loop awaited one fetch per target — bail if the user left mid-run.
    if (unmountedRef.current) return
    setQuote((prev) => ({
      ...prev,
      lines: prev.lines.map((l) => updates.get(lineKey(l)) ?? l),
    }))
    // Pushing this config across the home is an explicit commit — Cancel
    // must not then unpick the row those settings came from while every
    // other row keeps them.
    panelSnapshot.current = source
    const noun = kind === 'WD' ? 'window' : 'door'
    setApplyNote(
      updates.size === 0
        ? `These settings couldn't be applied to your other ${noun}s — they take different screen types. Customise them individually.`
        : `Applied to ${updates.size} other ${noun}${updates.size === 1 ? '' : 's'}.`,
    )
    setApplyBusy(false)
  }

  if (loading && quote.lines.length === 0) {
    return (
      <div className="al-pricing">
        <h2>Select screens</h2>
        <p className="al-pricing__loading">Loading your installed windows and doors…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="al-pricing">
        <h2>Select screens</h2>
        <Alert variant="error" title="Couldn't load your items">
          {error}
        </Alert>
        <div className="al-sc__actions">
          <Button variant="secondary" onClick={() => navigate('/')}>
            Back
          </Button>
        </div>
      </div>
    )
  }

  // Fetched successfully but HANA had no items on any of the address's
  // orders — surface that instead of a silent empty list.
  if (quote.itemsLoaded && quote.lines.length === 0) {
    const debug = typeof window !== 'undefined' && Boolean(window.AL_WINDOWS_CONFIG?.debug)
    const loadStubs = () => {
      setQuote((prev) => ({
        ...prev,
        lines: STUB_ITEMS.map((item) => lineFromItem(item)),
        itemsLoaded: true,
        installCost: 250,
        customer: prev.customer ? { ...prev.customer, salesOrg: '4000' } : prev.customer,
      }))
    }
    return (
      <div className="al-pricing">
        <h2>No screens available</h2>
        <p className="m-bx4">
          We couldn&rsquo;t find any screen-eligible items for your address. Please call us on 1800
          ALWINDOWS for assistance.
        </p>
        <div className="al-sc__actions">
          <Button variant="outline" onClick={() => navigate('/')}>
            Back
          </Button>
          {debug ? (
            <Button variant="secondary" onClick={loadStubs}>
              Load stub items (debug)
            </Button>
          ) : null}
        </div>
      </div>
    )
  }

  // Group rows the way the live app presents them: "My windows (N)" then
  // "My doors (N)" (legacy EditorOverview.renderSection splits on ProdType).
  const windowRows = quote.lines.filter((line) => line.sourceItem.ProdType === 'WD')
  const doorRows = quote.lines.filter((line) => line.sourceItem.ProdType === 'DR')
  // Legacy silently dropped anything that wasn't WD/DR; keep such rows
  // visible in a trailing group instead so no screen-eligible item is lost.
  const otherRows = quote.lines.filter(
    (line) => line.sourceItem.ProdType !== 'WD' && line.sourceItem.ProdType !== 'DR',
  )

  // Header facts, derived the same way legacy seeded its redux store from
  // the first item: FM_ALU_COLOUR Value → frame colour, BAL_RATING
  // Characteristic → BAL (PricingCalculator.loadData).
  const firstConfig = quote.lines[0]?.sourceConfig ?? []
  const frameColour = getConfigValue(firstConfig, 'FM_ALU_COLOUR')
  const balRating = getConfigCharacteristic(firstConfig, 'BAL_RATING')
  // Prefix "Lot {n}" when the property was identified by a lot number — for
  // new-build lot searches it's the only identifier (no street number), and
  // for multi-lot addresses it confirms which one was picked. Skip if `street`
  // already leads with "Lot" so we don't double it up.
  const lot = quote.customer?.lot?.trim()
  const street = quote.customer?.street ?? ''
  const homeAddress = [
    lot && !/^lot\b/i.test(street) ? `Lot ${lot}` : '',
    street,
    quote.customer?.city,
    [quote.customer?.state, quote.customer?.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  const salesOrg = quote.customer?.salesOrg ?? ''
  const selected = quote.lines.filter((l) => l.selected)
  const unconfigured = selected.filter((l) => !l.screenOption)
  const subtotal = selected.reduce((s, l) => s + lineTotal(l), 0)
  // Applies once something is configured; doubles for INSTALL_DOUBLE_FAM.
  const installCost = effectiveInstallCost(quote)
  const total = subtotal + installCost

  const renderRow = (line: ScreenLine) => {
    const item = line.sourceItem
    const key = lineKey(line)
    const name = lineDisplayName(line)
    const imgSrc = getScreenImageSrc(item.Image, item.IsCharactImage)
    // Legacy ScreenCard: DimReplaceText (e.g. "Non-standard opening") is
    // shown INSTEAD of Height × Width when SAP provides it.
    const dims =
      item.DimReplaceText?.trim() ||
      (getConfigValue(line.sourceConfig, 'FRAME_WID') &&
        `${getConfigValue(line.sourceConfig, 'FRAME_WID')} × ${getConfigValue(line.sourceConfig, 'FRAME_HT')}`)
    // Fixed (non-openable) windows can't take a screen either — legacy
    // marked isFixedWindow rows not-applicable, same as no ScreenApply.
    const disabled = !item.ScreenApply || item.FixedWindow
    const configured = line.screenOption !== ''
    const expanded = expandedKey === key
    const checkboxId = `al-pricing-check-${key}`
    // Apply-to-all targets: every other customisable item of the same kind.
    const applyTargets = quote.lines.filter(
      (l) =>
        lineKey(l) !== key &&
        l.sourceItem.ProdType === item.ProdType &&
        l.sourceItem.ScreenApply &&
        !l.sourceItem.FixedWindow &&
        prodFamFromConfig(l.sourceConfig) !== 'WBF',
    )
    const noun = item.ProdType === 'WD' ? 'windows' : 'doors'
    return (
      <li
        key={key}
        className={`no-m no-p al-pricing__item ${disabled ? 'al-pricing__item--disabled' : ''} ${expanded ? 'is-expanded' : ''}`}
      >
        <div className="al-pricing__row">
          {/* Theme-styled checkbox: real input positioned offscreen, the
              visible faux box is rendered via `::after` on the adjacent
              label (matches the spare-parts CatalogFilters pattern, see
              core.css `.input-checkbox + label::after`). Title text is
              visually hidden — the row's <h4> is the real label. */}

          <input
            id={checkboxId}
            type="checkbox"
            className="input-checkbox"
            disabled={disabled}
            checked={line.selected}
            onChange={() => toggle(key)}
          />

          <label htmlFor={checkboxId} className="al-pricing__check no-m no-p">
            <span className="visuallyhidden">Order {name}</span>
          </label>
          <div className="al-pricing__img">{imgSrc ? <img src={imgSrc} alt="" /> : null}</div>
          <div className="al-pricing__body">
            {/* Customer-editable label (legacy RoomName) — lets them tell
                identical openings apart; submitted to SAP with the order.
                Blurring it empty restores the product-name default. */}
            <input
              type="text"
              className="al-pricing__room medium bold no-m"
              aria-label={`Name this ${item.ProdType === 'DR' ? 'door' : 'window'}`}
              value={line.roomName ?? name}
              maxLength={40}
              onChange={(e) => updateLine(key, { ...line, roomName: e.target.value })}
              onBlur={(e) => {
                // Restore the API's default tag (ProdDesc) when emptied;
                // product name only if SAP sent no tag.
                const next = e.target.value.trim()
                if (!next) {
                  updateLine(key, { ...line, roomName: item.ProdDesc || name })
                  return
                }
                // Legacy `edit_window_name` GTM event — fire on the committed
                // value (blur), not per keystroke.
                if (next !== (item.ProdDesc || name)) trackEditWindow(next)
              }}
            />
            <h4 className="al-pricing__name xsmall">{name}</h4>
            <p className="al-pricing__dims xsmall no-m">{dims || 'Dimensions not provided'}</p>
            {disabled ? (
              // Per-item Message from SAP when present (legacy ScreenCard);
              // generic fallback otherwise.
              <p className="al-pricing__nope xsmall">
                {item.Message?.trim() || 'Screens not available for this item.'}
              </p>
            ) : null}
          </div>
          <div className="al-pricing__edit">
            {!disabled && !expanded ? (
              <Button
                variant="outline"
                onClick={() => openPanel(key)}
                aria-expanded={expanded}
                aria-controls={`al-pricing-cfg-${key}`}
              >
                {configured ? 'Edit' : 'Customise'}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Collapsing configurator body — grid-template-rows animates to
            natural height. Only mounted when expanded so options/colours
            fetches don't fire eagerly for every row. Live mode: each change
            commits straight to the quote (auto-selecting the row) so the
            cart bar below tracks the price as the user picks options. */}
        <div id={`al-pricing-cfg-${key}`} className="al-pricing__cfg" aria-hidden={!expanded}>
          <div className="al-pricing__cfg-inner">
            {expanded ? (
              <>
                <LineConfigurator
                  line={line}
                  salesOrg={salesOrg}
                  state={quote.customer?.state ?? ''}
                  homeColourName={frameColour}
                  onChange={(next) => updateLine(key, { ...next, selected: true })}
                  onSave={(next) => updateLine(key, { ...next, selected: true })}
                  onCancel={cancelPanel}
                  onDone={() => openPanel(null)}
                />
                {configured && applyTargets.length > 0 ? (
                  <div className="al-pricing__apply m-tx2">
                    <Button variant="outline" disabled={applyBusy} onClick={() => applyToAll(line)}>
                      {applyBusy
                        ? 'Applying…'
                        : `Apply to all ${noun} in my home (${applyTargets.length} more)`}
                    </Button>
                    {applyNote ? (
                      <p role="status" className="xsmall dark-grey no-m-b m-tx1">
                        {applyNote}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </li>
    )
  }

  const renderSection = (title: string | null, rows: ScreenLine[]) =>
    rows.length === 0 ? null : (
      <section key={title ?? 'other'}>
        {title ? <h3 className="medium bold m-tx4 m-bx2">{title}</h3> : null}
        <ul className="al-pricing__items no-m-t">{rows.map(renderRow)}</ul>
      </section>
    )

  return (
    <div className="al-pricing">
      <h2>Select screens</h2>

      {/* Home address / frame colour / BAL header, ported from the legacy
          ScreenOrderDetails component. */}
      <div className="al-pricing__details m-bx4">
        {homeAddress ? (
          <p className="no-m-t m-bx2">
            <span className="bold">Home address</span>
            <br />
            {homeAddress}
          </p>
        ) : null}
        {frameColour ? (
          <p className="no-m-t m-bx2">
            <span className="bold">Window &amp; door frame colour</span>
            <br />
            {frameColour}
          </p>
        ) : null}
        {balRating ? (
          <p className="no-m-t m-bx2">
            <span className="bold">BAL</span>
            <br />
            {balRating}
          </p>
        ) : null}
      </div>

      <p>
        Tick the items you&rsquo;d like to order screens for, and customise each one to choose the
        screen type, mesh, and colour. Your total updates in the cart below as you go.
      </p>

      {renderSection(`My windows (${windowRows.length})`, windowRows)}
      {renderSection(`My doors (${doorRows.length})`, doorRows)}
      {renderSection(null, otherRows)}

      {/* Fixed cart bar — sticks to the bottom of the viewport while the
          list scrolls, with a live total of everything ticked. */}
      <div className="al-pricing__cartbar" role="region" aria-label="Order summary">
        <div className="al-pricing__cartbar-info">
          <p className="bold no-m">
            {selected.length} item{selected.length === 1 ? '' : 's'} selected
          </p>
          {unconfigured.length > 0 ? (
            <p className="al-pricing__cartbar-hint xxsmall no-m">
              Customise {unconfigured.length === 1 ? '1 item' : `${unconfigured.length} items`} to
              continue
            </p>
          ) : null}
        </div>
        {/* Total on one line, label ahead of the figure, with what's baked
            into it sitting directly underneath — the installation charge
            reads as part of this number, which it wasn't when it lived over
            on the left. */}
        <div className="al-pricing__cartbar-total text-right">
          <p className="al-pricing__cartbar-amount no-m">
            <span className="xxsmall uppercase">Total:</span>{' '}
            <span className="large bold" data-testid="cart-total">
              ${total.toFixed(2)}
            </span>
          </p>
          {installCost > 0 ? (
            <p className="al-pricing__cartbar-hint xxsmall no-m">
              Includes ${installCost.toFixed(2)} installation
            </p>
          ) : null}
        </div>
        <Button
          variant="primary"
          disabled={selected.length === 0 || unconfigured.length > 0}
          onClick={() => navigate(ROUTE_SUMMARY)}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}

function lineKey(line: ScreenLine): string {
  return `${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`
}

function lineFromItem(item: ScreenItem): ScreenLine {
  const config = item.ItemToItemConfig?.results ?? []
  const balRating = getConfigCharacteristic(config, 'BAL_RATING') || 'LOW'
  return {
    sourceItem: item,
    sourceConfig: config,
    selected: false,
    qty: item.Quantity || 1,
    // Seed the editable label with the API's own value — ProdDesc carries
    // the opening's tag from the original order ("W01"), which is exactly
    // what legacy used as the RoomName default. Fall back to the product
    // name only when SAP sends nothing (SAP rejects empty labels).
    roomName:
      item.ProdDesc ||
      getConfigValue(config, 'PROD_FAM') ||
      (item.ProdType === 'DR' ? 'Door' : 'Window'),
    // No colour CODE until a swatch is picked (or the configurator matches
    // the home colour's name to a swatch). Legacy sent ColourCode "" for
    // unconfigured lines and would not save a screen without a real
    // /v1/screen/colours code. Seeding the FM_ALU_COLOUR characteristic (a
    // different coding scheme) or an invented 'WH' here let non-SAP codes
    // reach the payload and let a screen save with no colour chosen.
    frameColour: '',
    // The colour NAME ("White") — what legacy sent SAP for unconfigured
    // lines; replaced with the picked swatch's Desc on configure.
    frameColourDesc: getConfigValue(config, 'FM_ALU_COLOUR'),
    balRating,
    screenOption: '',
    meshType: '',
    grillType: '',
    petFlapType: '',
    hasTripleLock: false,
    unitPrice: 0,
    fitCost: 0,
    tripleLockCost: 0,
    nonTradeCharge: 0,
    meshPrice: 0,
    meshNonTrade: 0,
    grillPrice: 0,
    grillNonTrade: 0,
    petFlapPrice: 0,
    petFlapNonTrade: 0,
    screenOptionDesc: '',
    meshDesc: '',
    grillDesc: '',
    petFlapDesc: '',
    meshProdFam: '',
    grillProdFam: '',
    petFlapProdFam: '',
  }
}
